import React, { type FC, type ReactElement, Fragment } from 'react';
import cx from 'classnames';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import type { MFAError } from '../Login';
import styles from '../styles/MFA-login.scss';

dayjs.extend(duration);

interface MFAErrorSectionProps {
  mfaError: MFAError;
  navigateForgotPassword: React.MouseEventHandler<HTMLButtonElement>;
}

export const MFAErrorSection: FC<MFAErrorSectionProps> = ({ mfaError, navigateForgotPassword }) => {
  let primaryContent: string | ReactElement;
  let secondaryContent: string | ReactElement;
  let specialForgotPassword = false;

  switch (mfaError.key) {
    case 'bad_value':
      primaryContent = "The password or code is incorrect.";
      secondaryContent = (
        <Fragment key="bad_value_remaining">
          Please check your entry. You can try <strong>{mfaError.metadata.remaining}</strong> more times.
        </Fragment>
      );
      break;
    case 'timeout': {
      const timeoutInMinutes = dayjs.duration(mfaError.metadata.timeout, 'seconds').asMinutes();
      primaryContent = "Too many log in attempts.";
      secondaryContent = `To protect your account, it has been locked for ${timeoutInMinutes} minutes.`;
      break;
    }
    case 'account_locked':
      specialForgotPassword = true;
      primaryContent = "We locked your account to ensure it is you logging in.";
      secondaryContent = (
        <Fragment key="account_locked_reset">
          You can unlock your account by resetting your password using
          <button className={styles.forgotPasswordLink} onClick={navigateForgotPassword}>Forgot password</button>
          .
        </Fragment>
      );
      break;
    case 'throttled':
      primaryContent = "Too many log in attempts.";
      secondaryContent = "You have made too many requests to login in quick succession. Try again later.";
      break;
  }

  return (
    <div className={styles.errorContainer}>
      <p className={styles.errorPrimary}>
        {primaryContent}
      </p>
      <p className={cx(specialForgotPassword ? styles.forgotPasswordSecondary : styles.errorSecondary)}>
        {secondaryContent}
      </p>
    </div>
  );
};
